import React, { startTransition, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import Swal from "sweetalert2";
import { FaArchive, FaCheckSquare, FaChevronLeft, FaChevronRight, FaCopy, FaDownload, FaSave, FaSearch, FaSyncAlt, FaTimes, FaTrash } from "react-icons/fa";
import { FiCheck, FiChevronDown, FiChevronUp, FiCopy, FiEdit3, FiFileText, FiLoader, FiSliders, FiX } from "react-icons/fi";
import { bulkDeleteHandoutRecords, bulkDeleteExamRecords, deleteHandoutRecord, getHandoutRecord, listHandoutRecords, updateHandoutRecord, deleteExamRecord, getExamRecord, listExamRecords, updateExamRecord, downloadExamRecord, downloadHandoutRecord } from "../../api/mathBank";
import MathText from "./MathText";
import MarkdownMathText from "./MarkdownMathText";
import HandoutFocusContent from "./HandoutFocusContent";
import { groupHandoutConceptSections } from "../../features/handoutConceptGroups";
import QuestionAssetPreviewStrip from "../document-processing/QuestionAssetPreviewStrip";
import QuestionFilterSelect from "./QuestionFilterSelect";
import RecordHandwriting from "./RecordHandwriting";
import PagedRecordPreview from "./PagedRecordPreview";
import PreviewUuidTools from "./PreviewUuidTools";
import LoadingText from "../LoadingText";
import WordExportProgressModal from "../WordExportProgressModal";
import { chooseMixedWordExport } from "../../shared/chooseMixedWordExport";

const pageSize = 20;
const firstPreviewBatchSize = 3;
const formatStorage = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let size = bytes / 1024;
  let index = 0;
  while (size >= 1024 && index < units.length - 1) { size /= 1024; index += 1; }
  return `${size.toFixed(size >= 10 ? 1 : 2)} ${units[index]}`;
};
const previewPrefetchLifetime = 15000;
const formatDate = (value) => new Date(value).toLocaleString("zh-TW", { timeZone: "Asia/Taipei", hour12: false });
const versionLabels = { mixed: "教用學用混合", teaching: "教用詳解", student: "學用（僅題目）", teaching_brief: "教用簡答", teaching_detailed: "教用詳解" };
const recordExportModes = ["mixed", "teaching_brief", "teaching_detailed", "student"];
const examExportModes = recordExportModes.filter((mode) => mode !== "teaching");
const bilingualTemplateLabels = { bilingual_exam_paper: "雙語・英文在上", bilingual_chinese_first_exam_paper: "雙語・中文在上" };
const recordApis = {
  handout: { list: listHandoutRecords, get: getHandoutRecord, update: updateHandoutRecord, remove: deleteHandoutRecord, bulkRemove: bulkDeleteHandoutRecords },
  exam: { list: listExamRecords, get: getExamRecord, update: updateExamRecord, remove: deleteExamRecord, bulkRemove: bulkDeleteExamRecords },
};

const markdownSource = (value) => Array.isArray(value) ? value.join("\n") : String(value ?? "");

function CopyableUuid({ value, label = `複製題目 UUID ${value}` }) {
  const [feedback, setFeedback] = useState("");
  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(""), 2000);
    return () => clearTimeout(timer);
  }, [feedback]);
  if (!value) return <RecordId>尚未連結題庫</RecordId>;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setFeedback("copied");
    } catch { setFeedback("error"); }
  };
  return <UuidGroup>
    <CopyUuidButton type="button" aria-label={label} title={feedback === "copied" ? "已複製" : label} onClick={copy}>
      {feedback === "copied" ? <FiCheck aria-hidden="true" /> : <FiCopy aria-hidden="true" />}
    </CopyUuidButton>
    <RecordId>{value}</RecordId>
    {feedback === "copied" && <CopyFeedback role="status">已複製 UUID {value}</CopyFeedback>}
    {feedback === "error" && <CopyError role="alert">無法複製，請確認剪貼簿權限。</CopyError>}
  </UuidGroup>;
}

function HandoutQuestionSnapshot({ item, showImageCaptions = true, showSourceLabels = true, onEditQuestion }) {
  const snapshot = item.snapshot || {};
  const status = item.current_status === undefined ? snapshot.status : item.current_status;
  const reviewNote = item.current_review_note === undefined ? snapshot.review_note : item.current_review_note;
  const canEdit = Boolean(onEditQuestion && item.question_uuid && !item.is_deleted && !item.is_unlinked);
  const assets = Array.isArray(snapshot.assets) ? snapshot.assets : [];
  const assetsForRole = (role) => assets.filter((asset) => (asset.role || "prompt") === role);
  return <SnapshotQuestion data-handout-question-uuid={item.question_uuid || snapshot.id || ""} data-handout-position={item.position}>
    <QuestionMeta>
      <span>第 {item.position} 題{item.is_deleted ? " · 原題已刪除" : ""}</span>
      {canEdit ? <QuestionEditButton type="button" $draft={status === "draft"} title={status === "draft" ? "草稿：編輯題目" : "編輯題目"} aria-label={`編輯第 ${item.position} 題${status === "draft" ? "（草稿）" : ""}`} onClick={() => onEditQuestion(item)}>
        <FiEdit3 aria-hidden="true" />
      </QuestionEditButton> : status === "draft" && <DraftBadge title="草稿" aria-label="草稿"><FiFileText aria-hidden="true" /></DraftBadge>}
      {showSourceLabels && <><CopyableUuid value={item.question_uuid} /><span>{snapshot.grade?.name} / {snapshot.unit?.name}</span></>}
    </QuestionMeta>
    {status === "draft" && reviewNote && <div role="note" data-record-export-exclude="true"><strong>草稿疑義／待處理：</strong>{reviewNote}</div>}
    <MarkdownMathText source={snapshot.prompt_md || ""} />
    <QuestionAssetPreviewStrip assets={assetsForRole("prompt")} showCaption={showImageCaptions} />
    {(snapshot.answer_md || assetsForRole("answer").length > 0) && <SnapshotSolution role="region" aria-label="答案">
      <h5>答案</h5><MarkdownMathText source={snapshot.answer_md || ""} />
      <QuestionAssetPreviewStrip assets={assetsForRole("answer")} showCaption={showImageCaptions} />
    </SnapshotSolution>}
    {(snapshot.solution_md || assetsForRole("solution").length > 0) && <SnapshotSolution role="region" aria-label="詳解">
      <h5>詳解</h5><MarkdownMathText source={snapshot.solution_md || ""} />
      <QuestionAssetPreviewStrip assets={assetsForRole("solution")} showCaption={showImageCaptions} />
    </SnapshotSolution>}
  </SnapshotQuestion>;
}

function HandoutRecordPreview({ record }) {
  const payload = record.payload || {};
  const showImageCaptions = payload.showImageCaptions !== false;
  const showSourceLabels = payload.displayPreferences?.sourceLabels !== false;
  const items = record.items || [];
  const sections = groupHandoutConceptSections(payload.sections);
  const usedItems = new Set();
  const content = sections.map((section, sectionIndex) => {
    const key = `${section.id || section.type}-${sectionIndex}`;
    if (section.type === "focus") {
      const fallback = section.id === "focus" ? payload.options?.focus?.lines : "";
      const source = markdownSource(section.markdown ?? section.lines ?? fallback);
      return <RecordFocus key={key} data-handout-section-id={section.id} data-record-block>
        <RecordSectionTitle><MathText source={section.title || "重點整理"} fallback="" /></RecordSectionTitle>
        <HandoutFocusContent section={section} source={source} />
      </RecordFocus>;
    }
    if (section.type === "questions") {
      const references = Array.isArray(section.questions) ? section.questions
        : Array.isArray(section.question_ids) ? section.question_ids : [];
      if (!references.length) return null;
      return <RecordQuestionSection key={key} data-handout-section-id={section.id}>
        {section.title && <RecordSectionTitle><MathText source={section.title} fallback="" /></RecordSectionTitle>}
        {references.map((reference, referenceIndex) => {
          const id = String(typeof reference === "string" ? reference : reference?.id || reference?.question_uuid || "");
          const matches = (item) => id && (String(item.question_uuid || "") === id || String(item.snapshot?.id || "") === id);
          let itemIndex = items.findIndex((item, index) => !usedItems.has(index) && matches(item));
          if (itemIndex < 0) itemIndex = items.findIndex(matches);
          if (itemIndex < 0) return record.items_has_more ? null : <Message key={referenceIndex} $error role="status">此題的紀錄快照尚未找到。<RecordId>{id}</RecordId></Message>;
          usedItems.add(itemIndex);
          return <HandoutQuestionSnapshot key={referenceIndex} item={items[itemIndex]} showImageCaptions={showImageCaptions} showSourceLabels={showSourceLabels} />;
        })}
      </RecordQuestionSection>;
    }
    const pythonImage = section.asset?.source_language === "python" && String(section.asset.source_code || "").trim();
    if (section.type === "image" && (section.asset?.url || pythonImage)) return <RecordImage key={key} data-handout-section-id={section.id}>
      {pythonImage ? <QuestionAssetPreviewStrip assets={[section.asset]} showCaption={showImageCaptions} />
        : <img src={section.asset.url} alt={section.title || "講義圖片"} />}
      {section.text && <MarkdownMathText source={markdownSource(section.text)} />}
    </RecordImage>;
    if (section.type === "page_break" || section.type === "section_break") return <RecordBreak key={key} aria-label="分頁" data-record-page-break />;
    return null;
  });
  const remainingItems = items.filter((_, index) => !usedItems.has(index));
  return <RecordPaper aria-label="講義內容預覽">
    <RecordPaperTitle><MathText source={payload.options?.focus?.unitTitle || record.title} fallback="" /></RecordPaperTitle>
    {content}
    {remainingItems.length > 0 && <RecordQuestionSection>
      {sections.length > 0 && <RecordSectionTitle>其他題目</RecordSectionTitle>}
      {remainingItems.map((item, index) => <HandoutQuestionSnapshot key={`${item.position}-${index}`} item={item} showImageCaptions={showImageCaptions} showSourceLabels={showSourceLabels} />)}
    </RecordQuestionSection>}
  </RecordPaper>;
}

export default function HandoutRecords({ subject, authToken, grades = [], onLoad, onEditQuestion, renderHandoutPreview, recordKind = "handout" }) {
  const isExam = recordKind === "exam";
  const label = isExam ? "考卷" : "講義";
  const api = recordApis[recordKind];
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [gradeId, setGradeId] = useState("");
  const [status, setStatus] = useState("");
  const [offset, setOffset] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const [records, setRecords] = useState([]);
  const [count, setCount] = useState(0);
  const [storageBytes, setStorageBytes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [batchMode, setBatchMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailLoadingItems, setDetailLoadingItems] = useState(false);
  const [detailItemsError, setDetailItemsError] = useState("");
  const [openingRecord, setOpeningRecord] = useState(null);
  const [loadingRecord, setLoadingRecord] = useState(null);
  const [deletingRecord, setDeletingRecord] = useState(null);
  const recordDeletePending = useRef(false);
  const recordLoadPending = useRef(false);
  const recordOperationDialogRef = useRef(null);
  const [handwriting, setHandwriting] = useState(false);
  const [recordControlsOpen, setRecordControlsOpen] = useState(false);
  const [readerChromeHidden, setReaderChromeHidden] = useState(false);
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [notice, setNotice] = useState(null);
  const [exportRecord, setExportRecord] = useState(null);
  const [wordExport, setWordExport] = useState(null);
  const wordExportPending = useRef(null);
  const detailRequest = useRef(0);
  const detailAbort = useRef(null);
  const previewPrefetch = useRef(new Map());
  const dialogRef = useRef(null);
  const triggerRef = useRef(null);
  const readerHeaderRef = useRef(null);
  const revealChromeRef = useRef(null);
  const hideChromeRef = useRef(null);
  const keyboardInteraction = useRef(false);
  const wasChromeHidden = useRef(false);
  const readerHideFrame = useRef(null);
  const selectAllRef = useRef(null);
  const detailId = detail?.id || openingRecord?.id;
  const usesHandoutPreview = !isExam && detail?.workspace === "handout";
  const chromeHidden = readerChromeHidden && usesHandoutPreview && !handwriting && !recordControlsOpen;
  const selectedRecords = records.filter((record) => selectedIds.includes(record.id));
  const selectedCount = selectedRecords.length;
  const allSelected = records.length > 0 && selectedCount === records.length;
  const recordOperationPending = Boolean(loadingRecord?.fetching || deletingRecord);

  useEffect(() => { setRecordControlsOpen(false); setReaderChromeHidden(false); keyboardInteraction.current = false; }, [detailId]);

  useEffect(() => {
    if (chromeHidden && readerHeaderRef.current?.contains(document.activeElement)) {
      const readingContent = dialogRef.current?.querySelector("[data-record-reader-content]");
      (readingContent || revealChromeRef.current)?.focus({ preventScroll: true });
    } else if (!chromeHidden && wasChromeHidden.current) {
      hideChromeRef.current?.focus({ preventScroll: true });
    }
    wasChromeHidden.current = chromeHidden;
  }, [chromeHidden]);

  useEffect(() => {
    if (!recordOperationPending) return undefined;
    const previousFocus = document.activeElement;
    const dialog = recordOperationDialogRef.current;
    const blockKeyboard = (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      dialog?.focus({ preventScroll: true });
    };
    window.addEventListener("keydown", blockKeyboard, true);
    dialog?.focus({ preventScroll: true });
    return () => {
      window.removeEventListener("keydown", blockKeyboard, true);
      // The replacement confirmation may already own focus when fetching ends.
      if (!Swal.isVisible?.() && previousFocus?.isConnected) previousFocus.focus?.({ preventScroll: true });
    };
  }, [recordOperationPending]);

  const toggleReaderChrome = () => setReaderChromeHidden((value) => !value);
  const readerActivity = (event) => {
    if (!usesHandoutPreview || handwriting || recordControlsOpen || chromeHidden) return;
    if (event?.type === "wheel" && (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey ||
      !event.deltaY || Math.abs(event.deltaX) >= Math.abs(event.deltaY))) return;
    if (event?.target?.closest('button, input, textarea, select, a, [role="button"]')) return;
    if (keyboardInteraction.current && document.activeElement?.closest('button, input, textarea, select, a, [role="button"]')) return;
    if (event?.target?.closest('[data-reader-native-scroll]')) {
      // Let the browser apply the wheel/touch movement before resizing chrome.
      // The continuous reader can then preserve the actual scroll anchor.
      if (readerHideFrame.current === null) readerHideFrame.current = requestAnimationFrame(() => {
        readerHideFrame.current = null;
        setReaderChromeHidden(true);
      });
    } else setReaderChromeHidden(true);
  };

  useEffect(() => () => {
    if (readerHideFrame.current !== null) cancelAnimationFrame(readerHideFrame.current);
  }, []);

  useEffect(() => { setSelectedIds([]); }, [subject, authToken, recordKind, query, gradeId, status, offset, refresh]);

  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = selectedCount > 0 && !allSelected;
  }, [selectedCount, allSelected, batchMode, loading]);

  useEffect(() => {
    if (!detailId) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.querySelector('[aria-label="關閉明細"]')?.focus();
    return () => { document.body.style.overflow = previousOverflow; triggerRef.current?.focus(); };
  }, [detailId]);

  useEffect(() => {
    if (detailId && !openingRecord) dialogRef.current?.querySelector('[aria-label="關閉明細"]')?.focus({ preventScroll: true });
  }, [detailId, openingRecord]);

  useEffect(() => {
    if (busy || search.trim() === query) return undefined;
    const timer = setTimeout(() => { setQuery(search.trim()); setOffset(0); }, 250);
    return () => clearTimeout(timer);
  }, [search, query, busy]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.list({ subject, authToken, params: { search: query, grade_id: gradeId, ...(status ? { status } : {}), limit: pageSize, offset, include_storage: true } })
      .then((result) => {
        if (!active) return;
        if (offset && offset >= result.count) {
          setOffset(Math.max(0, Math.floor((result.count - 1) / pageSize) * pageSize));
          return;
        }
        setRecords(result.results);
        setCount(result.count);
        setStorageBytes(Number.isFinite(result.storage_bytes) ? result.storage_bytes : null);
      })
      .catch((error) => {
        if (!active) return;
        setRecords([]);
        setCount(0);
        setStorageBytes(null);
        setNotice({ error: true, text: error.message || `${label}紀錄讀取失敗` });
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [subject, authToken, query, gradeId, status, offset, refresh, api, label]);

  useEffect(() => {
    const pendingPrefetch = previewPrefetch.current;
    setDetail(null);
    setOpeningRecord(null);
    setDetailLoadingItems(false);
    setDetailItemsError("");
    setWordExport(null);
    setBusy(false);
    return () => {
      const pendingExport = wordExportPending.current;
      wordExportPending.current = null;
      pendingExport?.controller.abort();
      detailRequest.current += 1;
      detailAbort.current?.abort();
      pendingPrefetch.forEach((entry) => entry.controller.abort());
      pendingPrefetch.clear();
    };
  }, [subject, authToken, recordKind]);

  const closeDetail = () => {
    detailRequest.current += 1;
    detailAbort.current?.abort();
    setOpeningRecord(null); setDetail(null); setBusy(false);
    setDetailLoadingItems(false); setDetailItemsError("");
  };

  const editRecordQuestion = (item) => {
    if (detailLoadingItems || detail?.items_has_more || !onEditQuestion || !item.question_uuid || item.is_deleted || item.is_unlinked) return;
    closeDetail();
    onEditQuestion({ id: item.question_uuid }, subject);
  };

  const previewKey = (record) => `${record.id}:${record.updated_at || record.created_at || ""}`;
  const prefetchDetail = (record) => {
    // Opening a handout refreshes its saved content; hovering must remain read-only.
    if (!isExam || busy || openingRecord) return;
    const key = previewKey(record);
    const cache = previewPrefetch.current;
    const current = cache.get(key);
    if (current && Date.now() - current.startedAt < previewPrefetchLifetime) return;
    current?.controller.abort();
    cache.delete(key);
    // Keep only two small batches in memory; never prefetch the entire record.
    if (cache.size >= 2) {
      const oldest = cache.keys().next().value;
      cache.get(oldest).controller.abort();
      cache.delete(oldest);
    }
    const controller = new AbortController();
    const entry = {
      controller, startedAt: Date.now(),
      promise: api.get({ subject, authToken, id: record.id, params: { items_limit: firstPreviewBatchSize, items_offset: 0 }, signal: controller.signal }),
    };
    cache.set(key, entry);
    entry.promise.catch(() => { if (cache.get(key) === entry) cache.delete(key); });
  };

  const openDetail = async (id, trigger) => {
    if (busy || openingRecord) return;
    detailAbort.current?.abort();
    const summary = records.find((record) => record.id === id) || { id, title: `${label}紀錄` };
    const key = previewKey(summary);
    let prefetched = previewPrefetch.current.get(key);
    previewPrefetch.current.delete(key);
    if (prefetched && (prefetched.controller.signal.aborted || Date.now() - prefetched.startedAt >= previewPrefetchLifetime)) {
      prefetched.controller.abort();
      prefetched = null;
    }
    const controller = prefetched?.controller || new AbortController();
    detailAbort.current = controller;
    setNotice(null);
    setDetail(null);
    setDetailLoadingItems(false);
    setDetailItemsError("");
    setOpeningRecord(summary);
    triggerRef.current = trigger;
    const requestId = ++detailRequest.current;
    setBusy(true);
    let firstBatchShown = false;
    try {
      let record = await (prefetched?.promise || api.get({ subject, authToken, id, params: { items_limit: firstPreviewBatchSize, items_offset: 0 }, signal: controller.signal }));
      if (requestId !== detailRequest.current) return;
      if (!record.items_has_more && Number.isInteger(record.items_count) && record.items?.length !== record.items_count) {
        throw new Error("題目尚未載齊，請重新載入紀錄。");
      }
      setDetail(record);
      setHandwriting(false);
      setTitle(record.title);
      setNotes(record.notes);
      setNotice(null);
      setOpeningRecord(null);
      firstBatchShown = true;
      setBusy(false);
      setDetailLoadingItems(Boolean(record.items_has_more));
      while (record.items_has_more) {
        const nextOffset = record.items_next_offset;
        if (!Number.isInteger(nextOffset) || nextOffset !== record.items.length) {
          throw new Error("題目分批讀取中斷，請重新載入紀錄。");
        }
        const batch = await api.get({
          subject, authToken, id, signal: controller.signal,
          params: { items_limit: 15, items_offset: nextOffset, items_revision: record.items_revision, include_payload: false },
        });
        if (requestId !== detailRequest.current) return;
        if (batch.id !== record.id || (record.items_revision && batch.items_revision !== record.items_revision) ||
            (batch.items_offset !== undefined && batch.items_offset !== nextOffset) ||
            !batch.items?.length || (batch.items_has_more && batch.items_next_offset <= nextOffset)) {
          throw new Error("紀錄內容已變更或載入不完整，請重新載入紀錄。");
        }
        const items = [...record.items, ...batch.items];
        if (!batch.items_has_more && Number.isInteger(batch.items_count) && items.length !== batch.items_count) {
          throw new Error("題目尚未載齊，請重新載入紀錄。");
        }
        record = { ...record, ...batch, items };
        const loaded = record;
        // Appending pages re-lays out the whole preview; keep paging and scrolling responsive.
        startTransition(() => setDetail(loaded));
      }
    } catch (error) {
      if (requestId === detailRequest.current && !controller.signal.aborted) {
        const message = error.message || `${label}紀錄載入失敗，請再試一次。`;
        setDetailItemsError(message);
        setNotice({ error: true, text: message });
      }
    } finally {
      if (requestId === detailRequest.current) {
        setOpeningRecord(null);
        if (!firstBatchShown) setBusy(false);
        setDetailLoadingItems(false);
      }
    }
  };

  const retryDetailItems = () => {
    if (detail?.id) openDetail(detail.id, triggerRef.current);
  };

  const editRecord = async (record, templateIdOverride) => {
    if (!onLoad || busy || recordLoadPending.current) return;
    recordLoadPending.current = true;
    setBusy(true);
    setLoadingRecord({ id: record.id, title: record.title, fetching: true });
    setNotice(null);
    try {
      const fullRecord = await api.get({ subject, authToken, id: record.id });
      // Questions deleted from the bank keep their saved snapshot and can be
      // edited; only legacy items without a question UUID cannot be placed.
      if (fullRecord.items?.some((item) => item.is_unlinked)) {
        setNotice({ error: true, text: templateIdOverride
          ? "部分題目缺少題目 ID，無法載入製作雙語版。"
          : "部分題目缺少題目 ID，無法載入編輯。" });
        return;
      }
      // Release the loading overlay before opening the editor or an unsaved-change prompt.
      setLoadingRecord((current) => current ? { ...current, fetching: false } : current);
      if (templateIdOverride) await onLoad(fullRecord, templateIdOverride);
      else await onLoad(fullRecord);
    } catch (error) {
      setNotice({ error: true, text: error.message || `${label}載入編輯失敗，請再試一次。` });
    } finally {
      recordLoadPending.current = false;
      setLoadingRecord(null);
      setBusy(false);
    }
  };

  const saveDetail = async () => {
    if (detailLoadingItems || detail?.items_has_more) return;
    setBusy(true);
    try {
      const record = await api.update({ subject, authToken, id: detail.id, data: { title: title.trim(), notes } });
      setDetail(record);
      setRefresh((value) => value + 1);
      setNotice({ text: `${label}名稱與備註已更新。` });
    } catch (error) { setNotice({ error: true, text: error.message }); }
    finally { setBusy(false); }
  };

  const removeRecord = async (record) => {
    if (busy || recordDeletePending.current) return;
    recordDeletePending.current = true;
    setBusy(true);
    try {
      const { isConfirmed } = await Swal.fire({
        title: `刪除${label}紀錄「${record.title}」？`, text: "題庫中的原題會保留。", icon: "warning",
        showCancelButton: true, reverseButtons: true, confirmButtonText: "刪除", cancelButtonText: "取消",
        confirmButtonColor: "#b3261e",
      });
      if (!isConfirmed) return;
      setNotice(null);
      setDeletingRecord({ ids: [record.id], title: record.title });
      await api.remove({ subject, authToken, id: record.id });
      if (detail?.id === record.id) closeDetail();
      setRefresh((value) => value + 1);
      setNotice({ text: `${label}紀錄已刪除。` });
    } catch (error) { setNotice({ error: true, text: error.message || `${label}紀錄刪除失敗，請重試。` }); }
    finally {
      recordDeletePending.current = false;
      setDeletingRecord(null);
      setBusy(false);
    }
  };

  const toggleSelected = (id) => setSelectedIds((current) => current.includes(id) ? current.filter((value) => value !== id) : [...current, id]);
  const togglePageSelection = () => setSelectedIds(allSelected ? [] : records.map((record) => record.id));

  const removeSelectedRecords = async () => {
    const ids = selectedRecords.map((record) => record.id);
    if (!ids.length || busy || loading || recordDeletePending.current) return;
    recordDeletePending.current = true;
    setBusy(true);
    try {
      const { isConfirmed } = await Swal.fire({
        title: `刪除 ${ids.length} 份${label}紀錄？`,
        text: `將刪除勾選的 ${ids.length} 份${label}紀錄，題庫中的原題會保留。`,
        icon: "warning", showCancelButton: true, reverseButtons: true,
        confirmButtonText: `刪除 ${ids.length} 份紀錄`, cancelButtonText: "取消", confirmButtonColor: "#b3261e",
      });
      if (!isConfirmed) return;
      setNotice(null);
      setDeletingRecord({ ids, title: `${ids.length} 份${label}紀錄` });
      const result = await api.bulkRemove({ subject, authToken, ids });
      if (ids.includes(detail?.id)) closeDetail();
      setSelectedIds([]);
      setRefresh((value) => value + 1);
      setNotice({ text: `已刪除 ${result?.deleted_count ?? ids.length} 份${label}紀錄，題庫原題已保留。` });
    } catch (error) { setNotice({ error: true, text: error.message || `${label}紀錄批量刪除失敗，請重試。` }); }
    finally {
      recordDeletePending.current = false;
      setDeletingRecord(null);
      setBusy(false);
    }
  };

  const copyIds = async () => {
    if (detailLoadingItems || detail?.items_has_more) return;
    try {
      await navigator.clipboard.writeText(detail.items.filter((item) => item.question_uuid).map((item) => item.question_uuid).join("\n"));
      setNotice({ text: "已依題序複製全部 UUID。" });
    } catch { setNotice({ error: true, text: "無法複製 UUID，請確認瀏覽器剪貼簿權限。" }); }
  };

  const download = async (record, mode) => {
    if (!record?.id || !mode || busy || wordExportPending.current) return;
    const request = { controller: new AbortController() };
    wordExportPending.current = request;
    setNotice(null);
    setBusy(true);
    try {
      const mixedVersion = mode === "mixed" ? await chooseMixedWordExport(request.controller.signal) : null;
      if (request.controller.signal.aborted || wordExportPending.current !== request || (mode === "mixed" && !mixedVersion)) return;
      const filename = mode === "mixed"
        ? `${record.title}_${mixedVersion === "both" ? "教用學用.zip" : mixedVersion === "teacher" ? "教用.docx" : "學用.docx"}`
        : `${record.title}_${versionLabels[mode]}.docx`;
      setWordExport({ filename, cancelling: false, progress: { status: "queued", stage: "queued", current: 0, total: record.question_count || record.items_count || 0 } });
      const blob = await (isExam ? downloadExamRecord : downloadHandoutRecord)({
        subject, authToken, id: record.id, mode, ...(mixedVersion ? { mixedVersion } : {}), signal: request.controller.signal,
        onProgress: (progress) => {
          if (wordExportPending.current !== request || request.controller.signal.aborted) return;
          setWordExport((current) => current ? { ...current, progress } : current);
        },
      });
      if (wordExportPending.current !== request) return;
      if (request.controller.signal.aborted) { setNotice({ text: "已取消 Word 匯出。" }); return; }
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setNotice({ text: mode === "mixed"
        ? mixedVersion === "both" ? "教用與學用兩份 Word 已打包下載。" : `${mixedVersion === "teacher" ? "教用" : "學用"} Word 已下載。`
        : `${versionLabels[mode]} Word 已下載。` });
    } catch (error) {
      if (wordExportPending.current !== request) return;
      if (request.controller.signal.aborted || error.cancelled) { setNotice({ text: error.cancellationUnconfirmed ? error.message : "已取消 Word 匯出。" }); return; }
      const needsPreview = error.code === "bilingual_preview_stale" || error.code === "bilingual_preview_required";
      setNotice({ error: true, text: needsPreview
        ? "雙語考卷需要重新確認英文翻譯。請按「載入製作」，再按「翻譯並預覽」後儲存或下載 Word。"
        : error.message || `${label}下載失敗` });
    }
    finally {
      if (wordExportPending.current === request) {
        wordExportPending.current = null;
        setWordExport(null);
        setBusy(false);
      }
    }
  };

  const cancelWordExport = () => {
    const request = wordExportPending.current;
    if (!request || request.controller.signal.aborted) return;
    setWordExport((current) => current ? { ...current, cancelling: true } : current);
    request.controller.abort();
  };

  const chooseRowExport = async (record, value) => {
    setExportRecord(null);
    if (value.startsWith("bilingual:")) {
      await editRecord(record, value.slice("bilingual:".length));
      return;
    }
    download(record, value);
  };

  const handleDialogKey = (event) => {
    if (event.key === "Escape") { event.stopPropagation(); return; }
    if (event.key !== "Tab") return;
    keyboardInteraction.current = true;
    const targets = Array.from(dialogRef.current?.querySelectorAll('button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), a[href], [tabindex="0"]') || [])
      .filter((target) => !target.closest('[hidden], [aria-hidden="true"]') && getComputedStyle(target).display !== "none");
    if (!targets?.length) return;
    if (event.shiftKey && document.activeElement === targets[0]) { event.preventDefault(); targets[targets.length - 1].focus(); }
    else if (!event.shiftKey && document.activeElement === targets[targets.length - 1]) { event.preventDefault(); targets[0].focus(); }
  };

  return <RecordsRoot>
    {recordOperationPending && createPortal(<RecordLoadOverlay>
      <RecordLoadPanel ref={recordOperationDialogRef} role="dialog" aria-modal="true" aria-label={`正在${deletingRecord ? "刪除" : "載入"}${label}`} aria-busy="true" tabIndex={-1}>
        <LoadingGlyph><LoadingSpinner aria-hidden="true" /></LoadingGlyph>
        <h2>正在{deletingRecord ? "刪除" : "載入"}{label}</h2>
        <p title={deletingRecord?.title || loadingRecord?.title}>{deletingRecord?.title || loadingRecord?.title}</p>
        <div role="status" aria-live="polite"><LoadingText>{deletingRecord ? "刪除處理中" : "讀取題目與設定中"}</LoadingText></div>
        <small>{deletingRecord ? "完成後會自動更新紀錄列表，請稍候" : "讀取完成後會進入編輯，請稍候"}</small>
      </RecordLoadPanel>
    </RecordLoadOverlay>, document.body)}
    {wordExport && <WordExportProgressModal filename={wordExport.filename} progress={wordExport.progress} onCancel={cancelWordExport} cancelling={wordExport.cancelling} />}
    <Overview><div><h2>{label}紀錄</h2><p>{isExam ? "最近建立的考卷排在最前面，可依年級尋找並下載混合版、教用與學用 Word。" : "搜尋歷次講義，可下載紀錄快照，或載入題庫挑題繼續製作。"}</p></div><Total><strong>{count}</strong><span>{query || gradeId || status ? "符合篩選" : "份紀錄"}</span>{storageBytes !== null && <span title="紀錄內容與題目快照的 JSON 大小，不含外部圖片及資料庫額外空間">內容容量 {formatStorage(storageBytes)}</span>}</Total></Overview>
    <Controls>
      <SearchField><FaSearch /><input aria-label={`搜尋${label}紀錄`} placeholder={`${label}名稱、備註或題目 UUID`} value={search} disabled={busy} onChange={(event) => { setSelectedIds([]); setSearch(event.target.value); }} />{search && <button type="button" aria-label="清除搜尋" disabled={busy} onClick={() => { setSelectedIds([]); setSearch(""); }}><FaTimes /></button>}</SearchField>
      <QuestionFilterSelect aria-label={`${label}年級`} value={gradeId} disabled={busy} onChange={(event) => { setGradeId(event.target.value); setOffset(0); }}>
        <option value="">全部年級</option>
        {grades.map((grade) => <option key={grade.id} value={grade.id}>{grade.name}</option>)}
      </QuestionFilterSelect>
      <QuestionFilterSelect aria-label="紀錄狀態" value={status} disabled={busy} onChange={(event) => { setStatus(event.target.value); setOffset(0); }}><option value="">全部狀態</option><option value="exported">已匯出</option><option value="saved">已儲存</option></QuestionFilterSelect>
      {(search || gradeId || status) && <Action type="button" disabled={busy} onClick={() => { setSelectedIds([]); setSearch(""); setGradeId(""); setStatus(""); setOffset(0); }}>清除篩選</Action>}
      <Action type="button" title={`重新整理${label}紀錄`} aria-label={`重新整理${label}紀錄`} disabled={loading || busy} onClick={() => setRefresh((value) => value + 1)}><FaSyncAlt /></Action>
    </Controls>
    <SelectionBar aria-label={`${label}紀錄批量操作`}>
      <Action type="button" aria-pressed={batchMode} disabled={loading || busy} onClick={() => { setBatchMode((value) => !value); setSelectedIds([]); }}><FaCheckSquare />{batchMode ? "完成勾選" : "批量勾選"}</Action>
      <SelectionSummary>
        {batchMode ? <><strong role="status" aria-live="polite">已勾選 {selectedCount} 份</strong><span>僅限本頁紀錄；切換頁面或篩選會清除勾選。</span></> : <span>勾選本頁紀錄，一次整理多份{label}。</span>}
      </SelectionSummary>
      {batchMode && <SelectionActions>
        <Action type="button" disabled={busy || loading || !records.length} onClick={togglePageSelection}>{allSelected ? "取消本頁全選" : "全選本頁"}</Action>
        <Action type="button" disabled={busy || loading || !selectedCount} onClick={() => setSelectedIds([])}>清除勾選</Action>
        <DeleteAction type="button" aria-busy={Boolean(deletingRecord)} disabled={busy || loading || !selectedCount} onClick={removeSelectedRecords}>{deletingRecord ? <><LoadingSpinner aria-hidden="true" />刪除中…</> : <><FaTrash />{`刪除勾選（${selectedCount}）`}</>}</DeleteAction>
      </SelectionActions>}
    </SelectionBar>
    {notice && !detail && <Message $error={notice.error} role={notice.error ? "alert" : "status"}>{notice.text}</Message>}
    <TableScroll aria-busy={loading || busy}>
      <table>
        <thead><tr>{batchMode && <th className="selection-cell" scope="col"><SelectionCheckbox><input ref={selectAllRef} type="checkbox" aria-label={`勾選本頁全部${label}紀錄`} checked={allSelected} disabled={busy || loading || !records.length} onChange={togglePageSelection} /></SelectionCheckbox></th>}<th>{label}名稱</th><th>來源</th><th>版本</th><th>題數</th><th scope="col" aria-sort="descending" title="依建立時間由新到舊排列">建立時間 <span aria-hidden="true">↓</span></th><th>操作</th></tr></thead>
        <tbody>
          {loading ? <tr><td colSpan={batchMode ? 7 : 6}><Empty role="status"><LoadingText>讀取紀錄中</LoadingText></Empty></td></tr> : !records.length ? <tr><td colSpan={batchMode ? 7 : 6}><Empty><FaArchive /><strong>{query || gradeId || status ? "找不到符合條件的紀錄" : `尚無${label}紀錄`}</strong><span>{query || gradeId || status ? "試試其他名稱、年級或 UUID。" : `儲存或匯出${label}後，紀錄會出現在這裡。`}</span></Empty></td></tr> : records.map((record) => <tr key={record.id} data-selected={batchMode && selectedIds.includes(record.id) ? "true" : undefined}>
            {batchMode && <td className="selection-cell"><SelectionCheckbox><input type="checkbox" aria-label={`勾選${label}紀錄「${record.title}」`} checked={selectedIds.includes(record.id)} disabled={busy || loading} onChange={() => toggleSelected(record.id)} /></SelectionCheckbox></td>}
            <td className="record-title"><RecordTitleRow><ViewAction type="button" onPointerEnter={() => prefetchDetail(record)} onPointerDown={() => prefetchDetail(record)} title={`查看 ${record.title}`} aria-label={`查看 ${record.title}`} aria-busy={openingRecord?.id === record.id} $opening={openingRecord?.id === record.id} disabled={busy} onClick={(event) => openDetail(record.id, event.currentTarget)}>{openingRecord?.id === record.id ? <LoadingSpinner aria-hidden="true" /> : <FiFileText aria-hidden="true" />}</ViewAction><strong>{record.title}</strong></RecordTitleRow><small>{record.filename}</small><small><CopyableUuid value={record.id} label={`複製${label}紀錄 UUID ${record.id}`} /></small></td>
            <td>{isExam ? "段考卷" : record.workspace === "handout" ? "講義製作" : "題庫挑題"}<small>{record.exported_at ? "已匯出" : "已儲存"}</small></td>
            <td><Badges>{isExam && bilingualTemplateLabels[record.template_id] && <Badge>{bilingualTemplateLabels[record.template_id]}</Badge>}{(record.export_versions || [record.output_mode]).map((version) => <Badge key={version}>{versionLabels[version] || version}</Badge>)}</Badges></td>
            <td>{record.question_count}{record.unlinked_question_count > 0 && <small>{record.unlinked_question_count} 題待連結</small>}</td>
            <td className="record-date">{formatDate(record.created_at)}</td>
            <td className="record-actions"><RowActions $compact>
              {onLoad && <Action type="button" title={`編輯 ${record.title}`} aria-label={`編輯 ${record.title}`} aria-busy={loadingRecord?.id === record.id} disabled={busy} onClick={() => editRecord(record)}>{loadingRecord?.id === record.id ? <><LoadingSpinner aria-hidden="true" />載入中…</> : <><FiEdit3 aria-hidden="true" />編輯</>}</Action>}

              <ExportAction type="button" aria-label={`匯出「${record.title}」Word：選擇版本`} disabled={busy} onClick={() => setExportRecord(record)}><FaDownload aria-hidden="true" />匯出 <span aria-hidden="true">⌄</span></ExportAction>
              <Action type="button" title={`刪除 ${record.title}`} aria-label={`刪除 ${record.title}`} aria-busy={Boolean(deletingRecord?.ids.includes(record.id))} disabled={busy} onClick={() => removeRecord(record)}>{deletingRecord?.ids.includes(record.id) ? <LoadingSpinner aria-hidden="true" /> : <FaTrash />}</Action>
            </RowActions></td>
          </tr>)}
        </tbody>
      </table>
    </TableScroll>
    <Pagination>
      <Action type="button" aria-label={`上一頁${label}紀錄`} title="上一頁" disabled={busy || loading || !offset} onClick={() => setOffset((value) => Math.max(0, value - pageSize))}><FaChevronLeft /></Action>
      <span>{count ? `${offset + 1} - ${Math.min(offset + pageSize, count)} / ${count}` : "0 / 0"}</span>
      <Action type="button" aria-label={`下一頁${label}紀錄`} title="下一頁" disabled={busy || loading || offset + pageSize >= count} onClick={() => setOffset((value) => value + pageSize)}><FaChevronRight /></Action>
    </Pagination>
    {exportRecord && createPortal(<ExportOverlay onMouseDown={(event) => { if (event.target === event.currentTarget) setExportRecord(null); }}>
      <ExportPanel role="dialog" aria-modal="true" aria-label={`匯出「${exportRecord.title}」`} onKeyDown={(event) => { if (event.key === "Escape") setExportRecord(null); }}>
        <ExportHeading><div><strong>匯出 Word</strong><span title={exportRecord.title}>{exportRecord.title}</span></div><button type="button" autoFocus aria-label="關閉匯出選單" onClick={() => setExportRecord(null)}><FiX /></button></ExportHeading>
        {isExam && bilingualTemplateLabels[exportRecord.template_id] && <ExportHint>目前版型：{bilingualTemplateLabels[exportRecord.template_id]}</ExportHint>}
        <ExportSectionLabel>選擇下載版本</ExportSectionLabel>
        <ExportChoices>{(isExam ? examExportModes : recordExportModes).map((mode) => <ExportChoice key={mode} type="button" disabled={busy} onClick={() => chooseRowExport(exportRecord, mode)}><span>{versionLabels[mode]}</span><FaDownload aria-hidden="true" /></ExportChoice>)}</ExportChoices>
        {isExam && onLoad && <><ExportSectionLabel>製作雙語版 <small>需翻譯並預覽</small></ExportSectionLabel><ExportChoices>
          <ExportChoice type="button" disabled={busy} onClick={() => chooseRowExport(exportRecord, "bilingual:bilingual_exam_paper")}><span>英文在上</span><span aria-hidden="true">→</span></ExportChoice>
          <ExportChoice type="button" disabled={busy} onClick={() => chooseRowExport(exportRecord, "bilingual:bilingual_chinese_first_exam_paper")}><span>中文在上</span><span aria-hidden="true">→</span></ExportChoice>
        </ExportChoices></>}
      </ExportPanel>
    </ExportOverlay>, document.body)}
    {openingRecord && !detail && createPortal(<DetailOverlay><Detail ref={dialogRef} role="dialog" aria-modal="true" aria-label={`${label}紀錄載入中`} aria-busy="true" onKeyDown={handleDialogKey}>
      <DetailHeader><HeaderTitle><h2 title={openingRecord.title}>{openingRecord.title}</h2><Metadata><span>{label}紀錄</span><span>正在載入內容</span></Metadata></HeaderTitle><HeaderActions><HeaderIcon type="button" title="關閉明細" aria-label="關閉明細" onClick={closeDetail}><FiX aria-hidden="true" /></HeaderIcon></HeaderActions></DetailHeader>
      <DetailScroll><LoadingPreview><LoadingHeading role="status" aria-live="polite"><LoadingGlyph><LoadingSpinner aria-hidden="true" /></LoadingGlyph><div><strong>正在開啟{label}</strong><span>讀取題目與內容，請稍候…</span></div></LoadingHeading><SkeletonPaper aria-hidden="true"><SkeletonLine $heading /><SkeletonLine $width="72%" /><SkeletonLine /><SkeletonLine $width="84%" /><SkeletonBlock /><SkeletonLine $width="68%" /><SkeletonLine /><SkeletonLine $width="82%" /></SkeletonPaper></LoadingPreview></DetailScroll>
    </Detail></DetailOverlay>, document.body)}
    {detail && createPortal(<DetailOverlay><Detail ref={dialogRef} role="dialog" aria-modal="true" aria-label={`${label}紀錄明細`} data-reader-chrome-hidden={chromeHidden} onKeyDown={handleDialogKey} onPointerDownCapture={() => { keyboardInteraction.current = false; }}>
      {chromeHidden && <ReaderFloatingControls aria-label="閱讀工具">
        <ReaderRevealEdge aria-hidden="true" onPointerEnter={(event) => { if (event.pointerType !== "touch") setReaderChromeHidden(false); }} />
        <RevealChromeButton ref={revealChromeRef} type="button" aria-label="顯示工具列" title="顯示工具列" onClick={() => setReaderChromeHidden(false)}><FiChevronDown aria-hidden="true" /><span>顯示工具列</span></RevealChromeButton>
        <FloatingClose type="button" aria-label="關閉明細" title="關閉明細" disabled={busy} onClick={closeDetail}><FiX aria-hidden="true" /></FloatingClose>
      </ReaderFloatingControls>}
      <DetailHeader ref={readerHeaderRef} hidden={chromeHidden}>
        <HeaderTitle><h2 title={detail.title}>{detail.title}</h2><Metadata><span><UuidLabel>UUID：</UuidLabel><CopyableUuid value={detail.id} label="複製紀錄 UUID" /></span><span>{detail.question_count} 題{detail.created_by_label ? ` · ${detail.created_by_label}` : ""}</span></Metadata></HeaderTitle>
        <HeaderActions>
          {onLoad && <ReaderAction type="button" title="編輯這份紀錄" aria-label="編輯這份紀錄" aria-busy={loadingRecord?.id === detail.id} disabled={busy || detail.items_has_more || detailLoadingItems || detail.items.some((item) => item.is_unlinked)} onClick={() => editRecord(detail)}>{loadingRecord?.id === detail.id ? <><LoadingSpinner aria-hidden="true" /><span>載入中…</span></> : <><FiEdit3 aria-hidden="true" /><span>編輯</span></>}</ReaderAction>}
          <ReaderAction type="button" title="下載 Word" aria-label="下載 Word" disabled={busy} onClick={() => setExportRecord(detail)}><FaDownload aria-hidden="true" /><span>下載</span></ReaderAction>
          <ReaderAction type="button" title="紀錄設定" aria-label="紀錄設定" aria-expanded={recordControlsOpen && !handwriting} aria-controls="record-detail-settings" disabled={handwriting} onClick={() => setRecordControlsOpen((value) => !value)}><FiSliders aria-hidden="true" /><span>設定</span></ReaderAction>
          {usesHandoutPreview && <HeaderIcon ref={hideChromeRef} type="button" title="隱藏工具列" aria-label="隱藏工具列" disabled={handwriting || recordControlsOpen} onClick={toggleReaderChrome}><FiChevronUp aria-hidden="true" /></HeaderIcon>}
          <HeaderIcon type="button" title={handwriting ? "關閉手寫模式" : "開啟手寫模式"} aria-label={handwriting ? "關閉手寫模式" : "開啟手寫模式"} aria-pressed={handwriting} disabled={busy} onClick={() => setHandwriting((value) => !value)}><FiEdit3 aria-hidden="true" /></HeaderIcon>
          <HeaderIcon type="button" title="關閉明細" aria-label="關閉明細" disabled={busy} onClick={closeDetail}><FiX aria-hidden="true" /></HeaderIcon>
        </HeaderActions>
      </DetailHeader>
      {(detailLoadingItems || detailItemsError) && <LoadingProgress $floating={chromeHidden} role={detailItemsError ? "alert" : "status"}>
        {detailLoadingItems ? <><LoadingSpinner aria-hidden="true" /><span>正在載入題目… 已載入 {detail.items.length} / {detail.items_count ?? detail.question_count} 題</span></> : <><span>{detailItemsError}</span><Action type="button" disabled={busy} onClick={retryDetailItems}>重新載入</Action></>}
      </LoadingProgress>}
      <ReaderBody>
      <DetailScroll $immersive={chromeHidden} data-record-scroll role="region" aria-label={`${label}閱讀區`} onWheelCapture={readerActivity} onTouchStartCapture={readerActivity}>

      {detail.unlinked_question_count > 0 && <Message role="status">{detail.unlinked_question_count} 題尚未連結題庫 UUID；原始題目與詳解已保存在考卷快照，下載會包含這些題目。</Message>}
      {notice && !detailItemsError && <Message $error={notice.error} role={notice.error ? "alert" : "status"}>{notice.text}</Message>}
      {detail.sync_failed && <Message $error role="status">題庫同步失敗，目前顯示上次儲存的內容。</Message>}
      {detail.items.some((item) => item.is_deleted) && <Message $error role="status">部分原題已從題庫刪除，會保留當時內容，可以照常編輯，但這些題目不會再跟題庫同步。</Message>}
      <PreviewUuidTools resetKey={detail.id} enabled={!renderHandoutPreview && !handwriting && usesHandoutPreview}>
      <RecordHandwriting key={`${subject}:${recordKind}:${detail.id}`} storageKey={`record-handwriting:${subject}:${recordKind}:${detail.id}`} active={handwriting} responsive={!handwriting && usesHandoutPreview} allowPdfExport={handwriting} documentTitle={detail.title}>
      <PagedRecordPreview key={`pages:${detail.id}`} enabled={usesHandoutPreview && !renderHandoutPreview && !handwriting}>
      {usesHandoutPreview ? (renderHandoutPreview ? renderHandoutPreview(detail, !handwriting, { chromeHidden, handwriting, onToggleChrome: toggleReaderChrome, onActivity: readerActivity }) : <HandoutRecordPreview record={detail} />) : <QuestionList>
        {detail.items.map((item) => <li key={item.position} data-record-question>
          {isExam ? <HandoutQuestionSnapshot item={item} showImageCaptions={detail.payload?.showImageCaptions !== false} showSourceLabels={detail.payload?.displayPreferences?.sourceLabels !== false} onEditQuestion={onEditQuestion && !detail.items_has_more && !detailLoadingItems ? editRecordQuestion : undefined} /> : <>
            <QuestionMeta><span>第 {item.position} 題{item.is_deleted ? " · 原題已刪除" : ""}</span><CopyableUuid value={item.question_uuid} /><span>{item.snapshot.grade?.name} / {item.snapshot.unit?.name}</span></QuestionMeta>
            <MathText source={item.snapshot.prompt_md} fallback="" />
          </>}
        </li>)}
      </QuestionList>}
      </PagedRecordPreview>
      </RecordHandwriting>
      </PreviewUuidTools>
      </DetailScroll>
      {recordControlsOpen && !handwriting && <SettingsBackdrop type="button" aria-label="收起紀錄設定" onClick={() => setRecordControlsOpen(false)} />}
      <DetailControls id="record-detail-settings" role="region" aria-label="紀錄設定面板" hidden={!recordControlsOpen || handwriting}>
      <SettingsHeading><strong>紀錄設定</strong><HeaderIcon type="button" aria-label="關閉紀錄設定" onClick={() => setRecordControlsOpen(false)}><FiX aria-hidden="true" /></HeaderIcon></SettingsHeading>
      <SettingsInfo><span>紀錄 UUID</span><CopyableUuid value={detail.id} label="複製設定中的紀錄 UUID" /></SettingsInfo>
      <EditFields>
        <label>{label}名稱<input value={title} maxLength={180} disabled={busy || detail.items_has_more || detailLoadingItems} onChange={(event) => setTitle(event.target.value)} /></label>
        <label>備註<textarea value={notes} disabled={busy || detail.items_has_more || detailLoadingItems} onChange={(event) => setNotes(event.target.value)} rows={2} /></label>
      </EditFields>
      <RowActions>
        <Action type="button" disabled={busy || detail.items_has_more || detailLoadingItems || !title.trim()} onClick={saveDetail}><FaSave />儲存名稱與備註</Action>
        <Action type="button" disabled={busy || detail.items_has_more || detailLoadingItems} onClick={copyIds}><FaCopy />複製全部 UUID</Action>
        {onLoad && <Action type="button" title="載入後重新匯出會依題目 ID 讀取題庫現況" disabled={busy || detail.items_has_more || detailLoadingItems || detail.items.some((item) => item.is_unlinked)} onClick={() => editRecord(detail)}>{loadingRecord?.id === detail.id ? <><LoadingSpinner aria-hidden="true" />載入中…</> : <><FaArchive />載入製作</>}</Action>}
      </RowActions>
      <DownloadBar><span>{isExam ? "下載 Word" : "下載紀錄快照 Word"}</span>{(isExam ? examExportModes : recordExportModes).map((mode) => <Action key={mode} type="button" disabled={busy} onClick={() => download(detail, mode)}><FaDownload />{versionLabels[mode]}</Action>)}</DownloadBar>
      </DetailControls>
      </ReaderBody>
    </Detail></DetailOverlay>, document.body)}
  </RecordsRoot>;
}

const RecordsRoot = styled.section`--record-accent: var(--app-icon-accent, #365777); --record-soft: var(--app-surface-soft, #f1f4f8); min-width: 0; color: var(--app-text, #27313c);`;
const Overview = styled.div`display: flex; justify-content: space-between; align-items: center; gap: 24px; padding: 22px 0 8px; flex-wrap: wrap; h2 { font-size: 22px; margin: 0 0 8px; } p { margin: 0; font-size: 14px; opacity: .7; line-height: 1.6; }`;
const Total = styled.div`display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; strong { font-size: 30px; font-variant-numeric: tabular-nums; } span { font-size: 12px; opacity: .65; }`;
const Badges = styled.div`display: flex; flex-wrap: wrap; gap: 6px; min-width: 100px;`;
const Badge = styled.span`display: inline-flex; white-space: nowrap; border-radius: 20px; padding: 4px 8px; font-size: 12px; background: var(--record-soft); color: var(--app-text, #27313c);`;
const Empty = styled.div`display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 40px 16px; svg { font-size: 28px; opacity: .45; } span { font-size: 13px; opacity: .7; }`;
const DownloadBar = styled.div`display: flex; align-items: center; flex-wrap: wrap; gap: 8px; padding: 16px 0; margin-top: 16px; border-top: 1px solid var(--app-border, #d7dee4); > span { font-weight: 600; font-size: 13px; margin-right: 8px; }`;
const Controls = styled.div`
  display: flex; align-items: center; flex-wrap: wrap; gap: 12px; padding: 16px 0;
  select { max-width: 100%; padding: 9px; border: 1px solid var(--app-border, #d7dee4); border-radius: 4px; color: inherit; background: var(--app-surface, #fff); }
`;
const SearchField = styled.label`
  display: flex; align-items: center; gap: 8px; flex: 1 1 260px; padding: 9px 12px; border: 1px solid var(--app-border, #d7dee4); border-radius: 4px;
  input { flex: 1; min-width: 0; border: 0; outline: none; background: transparent; color: inherit; }
  &:focus-within { outline: 2px solid var(--record-accent); outline-offset: 2px; }
  button { border: 0; background: transparent; color: inherit; cursor: pointer; display: flex; padding: 4px; }
`;
const Action = styled.button`
  display: inline-flex; align-items: center; justify-content: center; gap: 7px; min-width: 36px; min-height: 36px; padding: 8px 10px; border: 1px solid var(--app-border, #d7dee4); border-radius: 4px; background: var(--app-surface, #fff); color: inherit; cursor: pointer;
  &:hover:not(:disabled) { background: var(--record-soft); }
  &:disabled { opacity: .45; cursor: default; }
  &:focus-visible { outline: 2px solid var(--record-accent); outline-offset: 2px; }
`;
const spin = keyframes`to { transform: rotate(360deg); }`;
const shimmer = keyframes`from { opacity: .45; } to { opacity: .9; }`;
const LoadingSpinner = styled(FiLoader)`animation: ${spin} 1s linear infinite; @media (prefers-reduced-motion: reduce) { animation: none; }`;
const ViewAction = styled(Action)`min-width: 28px; width: 28px; min-height: 28px; height: 28px; padding: 5px; flex-shrink: 0; border-radius: 6px; font-weight: 500; color: var(--record-accent); border-color: var(--app-border, #d7dee4); svg { width: 17px; height: 17px; } &[aria-busy="true"] { opacity: 1; cursor: wait; background: var(--record-soft); }`;
const RecordLoadOverlay = styled.div`position: fixed; inset: 0; z-index: 10030; display: grid; place-items: center; padding: 20px; background: var(--app-modal-overlay, rgba(30, 35, 40, .55));`;
const RecordLoadPanel = styled.div`
  box-sizing: border-box; width: min(100%, 380px); padding: 28px; border: 1px solid var(--app-border, #d7dee4); border-radius: 18px;
  display: flex; align-items: center; flex-direction: column; gap: 14px; text-align: center;
  background: var(--app-surface, #fff); color: var(--app-text, #27313c); box-shadow: 0 24px 70px #0003; outline: none;
  h2 { margin: 0; font-size: 20px; } p { margin: 0; max-width: 100%; overflow-wrap: anywhere; font-size: 14px; }
  small { color: var(--app-muted-text, #687380); font-size: 12px; line-height: 1.6; }
`;
const LoadingPreview = styled.div`width: min(100%, 760px); margin: 16px auto;`;
const LoadingHeading = styled.div`display: flex; align-items: center; gap: 15px; padding: 8px 0 24px; strong { display: block; font-size: 17px; } span { display: block; margin-top: 6px; font-size: 13px; color: var(--app-muted-text, #687380); }`;
const LoadingGlyph = styled.div`display: grid; place-items: center; width: 48px; height: 48px; flex: 0 0 auto; border: 1px solid var(--app-border, #d7dee4); border-radius: 14px; background: var(--app-surface, #fff); color: var(--record-accent); svg { width: 23px; height: 23px; }`;
const SkeletonPaper = styled.div`display: flex; flex-direction: column; gap: 18px; padding: 36px; border: 1px solid var(--app-border, #d7dee4); border-radius: 16px; background: var(--app-surface, #fff); box-shadow: 0 5px 24px #00000005; @media (max-width: 640px) { padding: 24px; gap: 15px; }`;
const SkeletonLine = styled.div`width: ${({ $width }) => $width || "100%"}; height: ${({ $heading }) => $heading ? "23px" : "12px"}; max-width: ${({ $heading }) => $heading ? "45%" : "none"}; margin-bottom: ${({ $heading }) => $heading ? "12px" : "0"}; border-radius: 5px; background: var(--app-surface-soft, #edf1f5); animation: ${shimmer} 1.4s ease-in-out infinite alternate; @media (prefers-reduced-motion: reduce) { animation: none; }`;
const SkeletonBlock = styled(SkeletonLine)`height: 128px; margin: 12px 0; border-radius: 10px;`;
const RowActions = styled.div`display: flex; align-items: center; flex-wrap: ${({ $compact }) => $compact ? "nowrap" : "wrap"}; gap: 8px;`;
const ExportAction = styled(Action)`min-height: 38px; border-radius: 9px; padding-inline: 14px; color: var(--record-accent); font-weight: 600; span { margin-left: 4px; }`;
const ExportOverlay = styled.div`position: fixed; inset: 0; z-index: 10020; display: grid; place-items: center; padding: 16px; background: rgba(20, 31, 40, .38);`;
const ExportPanel = styled.div`width: min(100%, 390px); max-height: min(90vh, 650px); overflow: auto; padding: 20px; border: 1px solid var(--app-border, #d7dee4); border-radius: 18px; background: var(--app-surface, #fff); box-shadow: 0 20px 60px #16232f30;`;
const ExportHeading = styled.div`display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; strong { display: block; font-size: 20px; } span { display: block; max-width: 310px; margin-top: 5px; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; color: var(--app-muted-text, #687380); font-size: 13px; } button { display: grid; place-items: center; width: 32px; height: 32px; flex: 0 0 auto; border: 0; border-radius: 8px; background: transparent; color: inherit; cursor: pointer; &:hover { background: var(--record-soft); } }`;
const ExportHint = styled.p`margin: 15px 0 0; padding: 9px 12px; border-radius: 9px; background: var(--record-soft); color: var(--record-accent); font-size: 13px;`;
const ExportSectionLabel = styled.div`margin: 20px 0 9px; color: var(--app-muted-text, #687380); font-size: 12px; font-weight: 700; letter-spacing: .04em; small { margin-left: 6px; font-size: 11px; font-weight: 400; letter-spacing: 0; }`;
const ExportChoices = styled.div`display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px;`;
const ExportChoice = styled.button`display: flex; align-items: center; justify-content: space-between; gap: 7px; min-height: 49px; padding: 10px 12px; border: 1px solid var(--app-border, #d7dee4); border-radius: 10px; background: var(--app-surface, #fff); color: var(--app-text, #27313c); text-align: left; cursor: pointer; &:hover:not(:disabled), &:focus-visible { border-color: var(--record-accent); background: var(--record-soft); outline: none; } &:disabled { opacity: .5; cursor: default; } svg { flex: 0 0 auto; color: var(--record-accent); }`;
const SelectionBar = styled.div`
  display: flex; align-items: center; flex-wrap: wrap; gap: 12px;
  margin-bottom: 12px; padding: 12px; border: 1px solid var(--app-border, #d7dee4);
  border-radius: 8px; background: var(--record-soft);
`;
const SelectionSummary = styled.div`
  flex: 1 1 220px; display: flex; flex-direction: column; gap: 4px;
  strong { font-size: 14px; font-variant-numeric: tabular-nums; }
  span { font-size: 12px; opacity: .75; line-height: 1.5; }
`;
const SelectionActions = styled.div`display: flex; flex-wrap: wrap; align-items: center; gap: 8px;`;
const DeleteAction = styled(Action)`
  color: var(--app-danger-text, #b3261e); border-color: var(--app-danger-border, #b3261e);
  &:hover:not(:disabled) { background: var(--app-danger-bg, #fff1ef); }
`;
const SelectionCheckbox = styled.label`
  display: flex; align-items: center; justify-content: center; min-width: 36px; min-height: 36px; cursor: pointer;
  input { width: 18px; height: 18px; margin: 0; accent-color: var(--record-accent); cursor: pointer; }
  input:disabled { cursor: default; }
  input:focus-visible { outline: 2px solid var(--record-accent); outline-offset: 3px; }
`;
const TableScroll = styled.div`
  overflow-x: auto; border: 1px solid var(--app-border, #d7dee4); border-radius: 10px; background: var(--app-surface, #fff);
  table { width: 100%; border-collapse: collapse; font-size: 14px; }
  th, td { text-align: left; padding: 12px 10px; border-bottom: 1px solid var(--app-border, #d7dee4); }
  th { font-weight: 600; white-space: nowrap; }
  thead { background: var(--record-soft); }
  tbody tr:hover { background: var(--app-surface-soft, #f5f8f7); }
  tbody tr[data-selected="true"] { background: var(--record-soft); box-shadow: inset 3px 0 var(--record-accent); }
  .selection-cell { width: 48px; padding: 8px 6px; }
  .record-title { min-width: 180px; max-width: 360px; overflow-wrap: anywhere; }
  .record-date { white-space: nowrap; }
  .record-actions { min-width: 290px; white-space: nowrap; }
  small { display: block; margin-top: 4px; font-size: 12px; opacity: .7; }
`;
const Pagination = styled.div`display: flex; align-items: center; justify-content: flex-end; gap: 12px; padding: 16px 0;`;
const Message = styled.div`margin: 12px 0; padding: 10px 12px; border-left: 3px solid ${({ $error }) => $error ? "var(--app-danger-text, #b3261e)" : "var(--record-accent)"}; background: ${({ $error }) => $error ? "var(--app-danger-bg, #fff1ef)" : "var(--record-soft)"}; color: ${({ $error }) => $error ? "var(--app-danger-text, #882018)" : "var(--record-accent)"};`;
const DetailOverlay = styled.div`position: fixed; inset: 0; z-index: 1500; display: flex; padding: 0; background: var(--app-modal-overlay, rgba(64, 64, 64, 0.72));`;
const Detail = styled.section`
  position: relative;
  display: flex; flex-direction: column; width: 100%; height: 100%; min-width: 0;
  max-height: 100dvh;
  overflow: hidden; box-sizing: border-box; color: var(--app-text, #27313c);
  background: var(--app-surface, #fff); border: 0; border-radius: 0;
`;
const DetailHeader = styled.div`
  &[hidden] { display: none; }
  display: flex; flex: 0 0 auto; align-items: center; justify-content: space-between; gap: 16px;
  position: relative; z-index: 20;
  padding: max(10px, env(safe-area-inset-top)) max(20px, env(safe-area-inset-right)) 10px max(20px, env(safe-area-inset-left));
  background: var(--app-surface, #fff); border-bottom: 1px solid var(--app-border, #d7dee4);
  h2 { font-size: 17px; margin: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  @media (max-width: 760px) { gap: 8px; padding-right: max(10px, env(safe-area-inset-right)); padding-left: max(10px, env(safe-area-inset-left)); h2 { font-size: 15px; } }
  @media (max-width: 540px) { h2 { font-size: 14px; } }
`;
const HeaderTitle = styled.div`min-width: 0; flex: 1;`;
const HeaderActions = styled.div`display: flex; align-items: center; gap: 4px; flex: 0 0 auto; @media (max-width: 420px) { margin-left: auto; }`;
const HeaderIcon = styled(Action)`
  width: 40px; height: 40px; padding: 9px; border-radius: 8px; border-color: transparent;
  svg { width: 20px; height: 20px; stroke-width: 1.8; }
  &[aria-pressed="true"] { background: var(--record-soft); color: var(--record-accent); border-color: var(--record-accent); }
`;
const ReaderAction = styled(Action)`
  min-height: 40px; border-color: transparent; border-radius: 8px; font-size: 13px; padding-inline: 10px;
  svg { width: 17px; height: 17px; flex: 0 0 auto; }
  &[aria-expanded="true"] { background: var(--record-soft); color: var(--record-accent); }
  @media (max-width: 760px) { width: 40px; padding: 9px; span { display: none; } }
  @media (max-width: 540px) { &[aria-label="編輯這份紀錄"], &[aria-label="下載 Word"] { display: none; } }
`;
const Metadata = styled.div`
  display: flex; align-items: center; flex-wrap: wrap; gap: 4px 14px; margin-top: 6px;
  font-size: 12px; color: var(--app-muted-text, #687380); overflow-wrap: anywhere;
  > span { display: inline-flex; align-items: center; min-width: 0; }
  code { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  @media (max-width: 760px) { font-size: 11px; gap: 2px 8px; > span:first-child { display: none; } }
`;
const ReaderFloatingControls = styled.div`
  position: absolute; z-index: 45; inset: max(8px, env(safe-area-inset-top)) max(8px, env(safe-area-inset-right)) auto max(8px, env(safe-area-inset-left));
  display: flex; align-items: center; justify-content: flex-start; pointer-events: none;
  > button { pointer-events: auto; background: color-mix(in srgb, var(--app-surface, #fff) 90%, transparent); box-shadow: 0 2px 12px #0002; backdrop-filter: blur(10px); }
`;
const ReaderRevealEdge = styled.div`position: fixed; inset: 0 0 auto; height: max(8px, env(safe-area-inset-top)); pointer-events: auto;`;
const RevealChromeButton = styled(Action)`
  min-height: 32px; padding: 5px 12px; border-radius: 20px; gap: 5px; font-size: 12px;
  @media (max-width: 540px) { width: 36px; height: 36px; padding: 6px; span { display: none; } }
  @media (pointer: coarse) { min-width: 44px; min-height: 44px; }
`;
const FloatingClose = styled(HeaderIcon)`
  position: absolute; right: 0; width: 34px; height: 34px; min-width: 34px; min-height: 34px; padding: 7px; border: 1px solid var(--app-border, #d7dee4); border-radius: 50%;
  @media (pointer: coarse) { width: 44px; height: 44px; }
`;
const ReaderBody = styled.div`position: relative; flex: 1; min-height: 0; min-width: 0; display: flex; overflow: hidden;`;
const LoadingProgress = styled.div`
  ${({ $floating }) => $floating && "position: absolute; z-index: 44; bottom: max(8px, env(safe-area-inset-bottom)); left: 50%; transform: translateX(-50%); max-width: calc(100% - 24px); border: 1px solid var(--app-border, #d7dee4); border-radius: 20px; box-shadow: 0 2px 10px #0002;"}
  display: flex; flex: 0 0 auto; align-items: center; gap: 8px; padding: 6px 18px; border-bottom: 1px solid var(--app-border, #d7dee4); background: var(--app-surface, #fff); font-size: 12px; color: var(--app-muted-text, #687380); > svg { flex: 0 0 auto; } > span { min-width: 0; } button { min-height: 28px; padding-block: 4px; margin-left: auto; }`;
const DetailScroll = styled.div`
  flex: 1; min-height: 0; min-width: 0; overflow: auto; overscroll-behavior: contain; scrollbar-gutter: stable;
  padding: 12px max(18px, env(safe-area-inset-right)) max(20px, env(safe-area-inset-bottom)) max(18px, env(safe-area-inset-left));
  background: var(--app-surface-soft, #e9edf2);
  @media (max-width: 760px) { padding: 4px max(4px, env(safe-area-inset-right)) max(4px, env(safe-area-inset-bottom)) max(4px, env(safe-area-inset-left)); }
  ${({ $immersive }) => $immersive && "padding: 0; scrollbar-gutter: auto; @media (max-width: 760px) { padding: 0; }"}
`;
const DetailControls = styled.aside`
  width: 300px; flex: 0 0 300px; box-sizing: border-box; padding: 12px 18px max(20px, env(safe-area-inset-bottom));
  overflow-y: auto; overscroll-behavior: contain; border-left: 1px solid var(--app-border, #d7dee4); background: var(--app-surface, #fff);
  &[hidden] { display: none; }
  ${RowActions} { align-items: stretch; flex-direction: column; > button { justify-content: flex-start; } }
  ${DownloadBar} { align-items: stretch; > span { width: 100%; } > button { flex: 1 1 100px; } }
  @media (max-width: 900px) { position: absolute; inset: 0 0 0 auto; z-index: 31; width: min(360px, 92%); box-shadow: -12px 0 32px #0002; }
`;
const SettingsHeading = styled.div`display: flex; align-items: center; justify-content: space-between; padding-bottom: 8px; border-bottom: 1px solid var(--app-border, #d7dee4); strong { font-size: 15px; }`;
const SettingsInfo = styled.div`display: grid; gap: 4px; padding-top: 14px; font-size: 12px; > span:first-child { color: var(--app-muted-text, #687380); }`;
const SettingsBackdrop = styled.button`display: none; @media (max-width: 900px) { display: block; position: absolute; inset: 0; z-index: 30; padding: 0; border: 0; background: #0005; cursor: pointer; }`;
const EditFields = styled.div`
  display: grid; grid-template-columns: 1fr; gap: 16px; margin: 16px 0;
  label { display: flex; flex-direction: column; gap: 6px; font-size: 13px; min-width: 0; }
  input, textarea { width: 100%; box-sizing: border-box; padding: 9px; border: 1px solid var(--app-border, #d7dee4); border-radius: 4px; background: var(--app-surface, #fff); color: inherit; font: inherit; }
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;
const QuestionList = styled.ol`list-style: none; padding: 24px 32px; margin: 0; background: var(--app-surface, #fff); border: 1px solid var(--app-border, #d7dee4); li { padding: 14px 0; border-bottom: 1px solid var(--app-border, #d7dee4); }`;
const QuestionMeta = styled.div`display: flex; flex-wrap: wrap; align-items: center; gap: 8px 16px; font-size: 12px; margin-bottom: 8px; code { overflow-wrap: anywhere; }`;
const DraftBadge = styled.span`display: inline-flex; align-items: center; justify-content: center; border-radius: 6px; width: 28px; height: 28px; color: var(--app-warning-text, #854d0e); background: var(--app-warning-bg, #fef3c7); font-size: 16px;`;
const QuestionEditButton = styled.button`display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border: 1px solid ${({ $draft }) => $draft ? "#eab308" : "var(--app-border, #d6dee8)"}; border-radius: 6px; padding: 5px; background: ${({ $draft }) => $draft ? "var(--app-warning-bg, #fef3c7)" : "var(--app-surface, #fff)"}; color: ${({ $draft }) => $draft ? "var(--app-warning-text, #854d0e)" : "var(--app-text, #27313c)"}; font-size: 16px; cursor: pointer; &:hover { filter: brightness(0.95); } &:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; } @media print { display: none; }`;
const RecordId = styled.code`color: var(--app-success-text, #198754); overflow-wrap: anywhere;`;
const UuidLabel = styled.span`white-space: nowrap; flex-shrink: 0;`;
const UuidGroup = styled.span`display: inline-flex; align-items: center; gap: 5px; min-width: 0;`;
const CopyUuidButton = styled.button.attrs({ "data-record-export-exclude": true })`
  display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto;
  width: 26px; height: 26px; padding: 5px; border: 0; border-radius: 6px;
  color: var(--app-muted-text, #687380); background: transparent; cursor: pointer;
  svg { width: 14px; height: 14px; stroke-width: 1.7; }
  &:hover { background: var(--record-soft); color: var(--app-text, #27313c); }
  &:focus-visible { outline: 2px solid var(--record-accent); outline-offset: 2px; }
`;
const CopyFeedback = styled.span`position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap;`;
const CopyError = styled.span`font-size: 11px; color: var(--app-danger-text, #b3261e);`;
const RecordPaper = styled.article`
  width: 100%; min-width: 0; box-sizing: border-box;
  margin: 0; padding: 32px; background: var(--app-surface, #fff);
  border: 1px solid var(--app-border, #d7dee4); font-size: 16px; line-height: 1.65;
  font-family: "標楷體", "DFKai-SB", "BiauKai", "Kaiti TC", "KaiTi", serif;
`;
const RecordPaperTitle = styled.div`margin-bottom: 20px; text-align: center; font-size: 22px; font-weight: 900;`;
const RecordSectionTitle = styled.h4`margin: 0 0 .5em; font-size: 1.1em; font-weight: 900; break-after: avoid;`;
const RecordFocus = styled.section`
  display: grid; min-width: 0; gap: .5em; margin: .8em 0 1.1em; padding: .8em .95em;
  background: var(--app-surface-soft, #f0f0f0); border-left: .32em solid #9ca3af;
`;
const RecordQuestionSection = styled.section`min-width: 0; margin: 1.2em 0;`;
const SnapshotQuestion = styled.div`
  min-width: 0; padding: 14px 0; border-bottom: 1px solid var(--app-border, #d7dee4);
  &:last-child { border-bottom: 0; }
  figure { width: min(100%, 460px); }
  figure img { max-height: none; }
`;
const SnapshotSolution = styled.div`
  margin-top: .8em; padding-left: .85em; border-left: 2px solid var(--app-border, #d7dee4);
  h5 { margin: 0 0 .3em; font-size: 1em; }
`;
const RecordImage = styled.figure`margin: 1em 0; text-align: center; img { max-width: 100%; height: auto; }`;
const RecordBreak = styled.hr`margin: 24px 0; border: 0; border-top: 2px dashed #9ca3af;`;

const RecordTitleRow = styled.div`display: flex; align-items: flex-start; gap: 8px; strong { padding-top: 3px; }`;
